import React, { useRef, useState, useEffect } from 'react';
import SelectSymbol from '../../components/SelectSymbol';
import SelectInterval from '../../private/Monitors/MonitorModal/SelectInterval';
import MonitorIndex from '../../private/Monitors/MonitorModal/MonitorIndex';
import SwitchInput from '../../components/SwitchInput';
import OrderType from '../../components/NewOrder/OrderType';
import SelectSide from '../../components/NewOrder/SelectSide';
import PriceTemplate from '../../private/OrderTemplates/PriceTemplate';
import QuantityTemplate from '../../private/OrderTemplates/QuantityTemplate';
import ConditionsArea from '../../private/Automations/AutomationModal/ConditionsArea/ConditionsArea';
import { getIndexes, getDefaultIndexes, getFuturesLastOrderIndexes, getFuturesLiquidationIndexes, getLastOrderIndexes } from '../../services/BeholderService';
import { getSymbol } from '../../services/SymbolsService';
import { saveMonitor } from '../../services/MonitorsService';
import { saveOrderTemplate } from '../../services/OrderTemplatesService';
import { saveAutomation } from '../../services/AutomationsService';
import { saveStrategy } from '../../services/StrategiesService';

/**
 * props:
 * - onNotification
 */
function StrategyWizard(props) {

    const DEFAULT_MONITOR = {
        symbol: '',
        type: "CANDLES",
        interval: "1m",
        indexes: '',
        isActive: false,
        logs: false
    }

    const DEFAULT_ORDER_TEMPLATE = {
        name: '',
        type: 'MARKET',
        side: 'BUY',
        limitPrice: '',
        limitPriceMultiplier: 1,
        stopPrice: '',
        stopPriceMultiplier: 1,
        quantity: '',
        quantityMultiplier: 1
    }

    const DEFAULT_AUTOMATION = {
        name: '',
        conditions: '',
        indexes: '',
        actions: [],
        isActive: false,
        logs: false
    }

    const DEFAULT_STRATEGY = {
        name: "",
        isActive: false
    }

    const [monitor, setMonitor] = useState(DEFAULT_MONITOR);
    const [buyOrderTemplate, setBuyOrderTemplate] = useState({ ...DEFAULT_ORDER_TEMPLATE, side: 'BUY' });
    const [sellOrderTemplate, setSellOrderTemplate] = useState({ ...DEFAULT_ORDER_TEMPLATE, side: 'SELL' });
    const [buyAutomation, setBuyAutomation] = useState({ ...DEFAULT_AUTOMATION, name: 'Automação de Compra' });
    const [sellAutomation, setSellAutomation] = useState({ ...DEFAULT_AUTOMATION, name: 'Automação de Venda' });
    const [strategy, setStrategy] = useState(DEFAULT_STRATEGY);

    const [indexes, setIndexes] = useState([]);
    const [symbol, setSymbol] = useState({});
    const [error, setError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [currentStep, setCurrentStep] = useState(1);

    const btnClose = useRef('');
    const btnSave = useRef('');

    useEffect(() => {
        const modal = document.getElementById('modalStrategyWizard');
        modal.addEventListener('hidden.bs.modal', (event) => {
            setMonitor(DEFAULT_MONITOR);
            setBuyOrderTemplate({ ...DEFAULT_ORDER_TEMPLATE, side: 'BUY' });
            setSellOrderTemplate({ ...DEFAULT_ORDER_TEMPLATE, side: 'SELL' });
            setBuyAutomation({ ...DEFAULT_AUTOMATION, name: 'Automação de Compra' });
            setSellAutomation({ ...DEFAULT_AUTOMATION, name: 'Automação de Venda' });
            setStrategy(DEFAULT_STRATEGY);
            setError('');
            setIsCreating(false);
            setCurrentStep(1);
        })
    }, [])

    useEffect(() => {
        if (!monitor.symbol) return;

        const monitorSymbol = monitor.symbol.toUpperCase();

        if (monitorSymbol.startsWith('*'))
            setSymbol({ base: '*', quote: monitorSymbol.replace('*', '') });
        else {
            getSymbol(monitorSymbol)
                .then(symbolObj => {
                    if (symbolObj)
                        setSymbol(symbolObj)
                    else
                        setIndexes(getDefaultIndexes(monitorSymbol))
                })
                .catch(err => {
                    console.error(err.response ? err.response.data : err.message);
                });
        }
    }, [monitor.symbol])

    useEffect(() => {
        if (!monitor.symbol) return;

        function removeRepeatedAndSort(indexes) {
            indexes = indexes.filter((item, index, self) =>
                index === self.findIndex(t => t.eval === item.eval)
            )
            return indexes.sort((a, b) => {
                if (a.variable > b.variable) return 1;
                return -1;
            })
        }

        function getWalletIndexes(indexes, symbol) {
            const walletIndexes = [];

            const baseWallet = indexes.find(ix => ix.variable.startsWith('WALLET_') && symbol.base === ix.symbol);
            if (baseWallet) walletIndexes.push(baseWallet);

            const quoteWallet = indexes.find(ix => ix.variable.startsWith('WALLET_') && symbol.quote === ix.symbol.replace('*', ''));
            if (quoteWallet) walletIndexes.push(quoteWallet);

            if (localStorage.getItem("hasFutures") === "true") {
                const baseFWallet = indexes.find(ix => ix.variable.startsWith('FWALLET_') && symbol.base === ix.symbol);
                if (baseFWallet) walletIndexes.push(baseFWallet);

                const quoteFWallet = indexes.find(ix => ix.variable.startsWith('FWALLET_') && symbol.quote === ix.symbol.replace('*', ''));
                if (quoteFWallet) walletIndexes.push(quoteFWallet);
            }

            return walletIndexes;
        }

        getIndexes()
            .then(indexes => {
                const isWildcard = symbol.base === '*';
                let filteredIndexes = isWildcard ? indexes.filter(k => k.symbol.endsWith(symbol.quote)) : indexes.filter(k => k.symbol === monitor.symbol);
                const defaultIndexes = getDefaultIndexes(monitor.symbol);

                if (isWildcard) {
                    filteredIndexes.forEach(ix => {
                        if (/^(F?WALLET_\d+)$/.test(ix.variable)) {
                            ix.symbol = ix.symbol.replace('*', '');
                            ix.eval = ix.eval.replace('*', '');
                        }
                        else {
                            ix.eval = ix.eval.replace(ix.symbol, monitor.symbol);
                            ix.symbol = monitor.symbol;
                        }
                    })
                }

                const lastOrderIndexes = getLastOrderIndexes(monitor.symbol);
                filteredIndexes.push(...lastOrderIndexes);
                filteredIndexes.push(...defaultIndexes);

                if (localStorage.getItem("hasFutures") === "true") {
                    const fLastOrderIndexes = getFuturesLastOrderIndexes(monitor.symbol);
                    filteredIndexes.push(...fLastOrderIndexes);

                    const liquidationIndexes = getFuturesLiquidationIndexes(monitor.symbol);
                    filteredIndexes.push(...liquidationIndexes);
                }

                filteredIndexes = removeRepeatedAndSort(filteredIndexes);

                if (!isWildcard) {
                    const walletIndexes = getWalletIndexes(indexes, symbol);
                    filteredIndexes.splice(0, 0, ...walletIndexes);
                }

                setIndexes(filteredIndexes);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
            })
    }, [symbol, monitor.symbol])

    function onMonitorChange(event) {
        setMonitor(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onBuyOrderTemplateChange(event) {
        setBuyOrderTemplate(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onSellOrderTemplateChange(event) {
        setSellOrderTemplate(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    function onBuyAutomationChange(event) {
        if (event.target.id === 'conditions' || event.target.id === 'indexes') {
            setBuyAutomation(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        } else {
            setBuyAutomation(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        }
    }

    function onSellAutomationChange(event) {
        if (event.target.id === 'conditions' || event.target.id === 'indexes') {
            setSellAutomation(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        } else {
            setSellAutomation(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
        }
    }

    function onStrategyChange(event) {
        setStrategy(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    async function onCreateCompleteStrategy() {
        if (!monitor.symbol || !monitor.interval) {
            setError('Símbolo e Intervalo são obrigatórios para o Monitor.');
            return;
        }

        if (!buyOrderTemplate.name || !sellOrderTemplate.name) {
            setError('Nome dos Templates de Ordem são obrigatórios.');
            return;
        }

        if (!buyAutomation.conditions || !sellAutomation.conditions) {
            setError('Condições das Automações são obrigatórias.');
            return;
        }

        if (!strategy.name) {
            setError('Nome da Estratégia é obrigatório.');
            return;
        }

        setIsCreating(true);
        setError('');

        try {
            // 1. Criar Monitor
            const monitorData = {
                ...monitor,
                symbol: monitor.symbol,
                type: 'CANDLES',
                interval: monitor.interval,
                indexes: monitor.indexes,
                isActive: monitor.isActive,
                logs: monitor.logs
            };
            const createdMonitor = await saveMonitor(0, monitorData);
            const monitorId = createdMonitor.id;

            // 2. Criar Order Templates
            const buyOTData = {
                ...buyOrderTemplate,
                symbol: monitor.symbol,
                name: buyOrderTemplate.name || `Compra ${monitor.symbol}`,
                side: 'BUY'
            };
            const createdBuyOT = await saveOrderTemplate(0, buyOTData);
            const buyOrderTemplateId = createdBuyOT.id;

            const sellOTData = {
                ...sellOrderTemplate,
                symbol: monitor.symbol,
                name: sellOrderTemplate.name || `Venda ${monitor.symbol}`,
                side: 'SELL'
            };
            const createdSellOT = await saveOrderTemplate(0, sellOTData);
            const sellOrderTemplateId = createdSellOT.id;

            // 3. Criar Automations
            const buyAutoData = {
                ...buyAutomation,
                symbol: monitor.symbol,
                name: buyAutomation.name || `Automação de Compra ${monitor.symbol}`,
                conditions: buyAutomation.conditions,
                indexes: buyAutomation.indexes || monitor.indexes,
                actions: [{
                    type: 'ORDER',
                    orderTemplateId: buyOrderTemplateId
                }],
                isActive: buyAutomation.isActive,
                logs: buyAutomation.logs
            };
            const createdBuyAuto = await saveAutomation(0, buyAutoData);
            const buyAutomationId = createdBuyAuto.id;

            const sellAutoData = {
                ...sellAutomation,
                symbol: monitor.symbol,
                name: sellAutomation.name || `Automação de Venda ${monitor.symbol}`,
                conditions: sellAutomation.conditions,
                indexes: sellAutomation.indexes || monitor.indexes,
                actions: [{
                    type: 'ORDER',
                    orderTemplateId: sellOrderTemplateId
                }],
                isActive: sellAutomation.isActive,
                logs: sellAutomation.logs
            };
            const createdSellAuto = await saveAutomation(0, sellAutoData);
            const sellAutomationId = createdSellAuto.id;

            // 4. Criar Strategy
            const strategyData = {
                ...strategy,
                symbol: monitor.symbol,
                name: strategy.name,
                monitorId: monitorId,
                buyAutomationId: buyAutomationId,
                sellAutomationId: sellAutomationId,
                isActive: strategy.isActive
            };
            await saveStrategy(0, strategyData);

            setIsCreating(false);
            btnClose.current.click();
            if (props.onNotification) {
                props.onNotification({ type: 'success', text: 'Estratégia completa criada com sucesso!' });
            }
            window.location.reload();
        } catch (err) {
            setIsCreating(false);
            const errorMsg = err.response ? err.response.data : err.message;
            setError(errorMsg);
            if (props.onNotification) {
                props.onNotification({ type: 'error', text: errorMsg });
            }
        }
    }

    return (
        <div className="modal fade" id="modalStrategyWizard" tabIndex="-1" role="dialog" aria-labelledby="modalTitleWizard" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleWizard">Criar Estratégia Completa</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        <ul className="nav nav-tabs" id="wizardTabs" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 1 ? 'active' : ''}`} onClick={() => setCurrentStep(1)}>1. Monitor</button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 2 ? 'active' : ''}`} onClick={() => setCurrentStep(2)}>2. Ordem de Compra</button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 3 ? 'active' : ''}`} onClick={() => setCurrentStep(3)}>3. Ordem de Venda</button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 4 ? 'active' : ''}`} onClick={() => setCurrentStep(4)}>4. Automação de Compra</button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 5 ? 'active' : ''}`} onClick={() => setCurrentStep(5)}>5. Automação de Venda</button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className={`nav-link ${currentStep === 6 ? 'active' : ''}`} onClick={() => setCurrentStep(6)}>6. Estratégia</button>
                            </li>
                        </ul>

                        <div className="tab-content p-3">
                            {currentStep === 1 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Monitor</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="symbol">Símbolo:</label>
                                            <SelectSymbol symbol={monitor.symbol} onChange={onMonitorChange} disabled={false} showAny={false} onlyFavorites={false} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <SelectInterval interval={monitor.interval} onChange={onMonitorChange} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <MonitorIndex indexes={monitor.indexes} onChange={onMonitorChange} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="isActive" text="Está Ativo?" onChange={onMonitorChange} isChecked={monitor.isActive} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="logs" text="Habilitar Logs?" onChange={onMonitorChange} isChecked={monitor.logs} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Template de Ordem de Compra</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name">Nome:</label>
                                            <input id="name" type="text" className="form-control" value={buyOrderTemplate.name || ''} onChange={onBuyOrderTemplateChange} placeholder="Template de Compra" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <OrderType type={buyOrderTemplate.type} onChange={onBuyOrderTemplateChange} isFuture={false} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <PriceTemplate id="limitPrice" text="Preço Limite:" symbol={monitor.symbol} onChange={onBuyOrderTemplateChange} price={buyOrderTemplate.limitPrice} multiplier={buyOrderTemplate.limitPriceMultiplier} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <PriceTemplate id="stopPrice" text="Preço Stop:" symbol={monitor.symbol} onChange={onBuyOrderTemplateChange} price={buyOrderTemplate.stopPrice} multiplier={buyOrderTemplate.stopPriceMultiplier} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <QuantityTemplate id="quantity" text="Quantidade:" isFuture={false} quantity={buyOrderTemplate.quantity} multiplier={buyOrderTemplate.quantityMultiplier} onChange={onBuyOrderTemplateChange} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 3 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Template de Ordem de Venda</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name">Nome:</label>
                                            <input id="name" type="text" className="form-control" value={sellOrderTemplate.name || ''} onChange={onSellOrderTemplateChange} placeholder="Template de Venda" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <OrderType type={sellOrderTemplate.type} onChange={onSellOrderTemplateChange} isFuture={false} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <PriceTemplate id="limitPrice" text="Preço Limite:" symbol={monitor.symbol} onChange={onSellOrderTemplateChange} price={sellOrderTemplate.limitPrice} multiplier={sellOrderTemplate.limitPriceMultiplier} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <PriceTemplate id="stopPrice" text="Preço Stop:" symbol={monitor.symbol} onChange={onSellOrderTemplateChange} price={sellOrderTemplate.stopPrice} multiplier={sellOrderTemplate.stopPriceMultiplier} />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <QuantityTemplate id="quantity" text="Quantidade:" isFuture={false} quantity={sellOrderTemplate.quantity} multiplier={sellOrderTemplate.quantityMultiplier} onChange={onSellOrderTemplateChange} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 4 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Automação de Compra</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name">Nome:</label>
                                            <input id="name" type="text" className="form-control" value={buyAutomation.name || ''} onChange={onBuyAutomationChange} placeholder="Automação de Compra" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <ConditionsArea symbol={monitor.symbol} conditions={buyAutomation.conditions} indexes={indexes} onChange={onBuyAutomationChange} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="isActive" text="Está Ativa?" onChange={onBuyAutomationChange} isChecked={buyAutomation.isActive} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="logs" text="Habilitar Logs?" onChange={onBuyAutomationChange} isChecked={buyAutomation.logs} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 5 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Automação de Venda</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name">Nome:</label>
                                            <input id="name" type="text" className="form-control" value={sellAutomation.name || ''} onChange={onSellAutomationChange} placeholder="Automação de Venda" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <ConditionsArea symbol={monitor.symbol} conditions={sellAutomation.conditions} indexes={indexes} onChange={onSellAutomationChange} />
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="isActive" text="Está Ativa?" onChange={onSellAutomationChange} isChecked={sellAutomation.isActive} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="logs" text="Habilitar Logs?" onChange={onSellAutomationChange} isChecked={sellAutomation.logs} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {currentStep === 6 && (
                                <div className="tab-pane fade show active">
                                    <h5 className="mb-3">Estratégia Final</h5>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="name">Nome:</label>
                                            <input id="name" type="text" className="form-control" value={strategy.name || ''} onChange={onStrategyChange} placeholder="Nome da Minha Estratégia" />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <SwitchInput id="isActive" text="Está Ativa?" onChange={onStrategyChange} isChecked={strategy.isActive} />
                                        </div>
                                    </div>
                                    <div className="alert alert-info mt-3">
                                        <strong>Resumo:</strong><br />
                                        Símbolo: {monitor.symbol || 'N/A'}<br />
                                        Monitor: {monitor.interval || 'N/A'} - {monitor.indexes ? monitor.indexes.split(',').length : 0} indicadores<br />
                                        Ordem de Compra: {buyOrderTemplate.name || 'N/A'}<br />
                                        Ordem de Venda: {sellOrderTemplate.name || 'N/A'}<br />
                                        Automação de Compra: {buyAutomation.name || 'N/A'}<br />
                                        Automação de Venda: {sellAutomation.name || 'N/A'}
                                    </div>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="alert alert-danger mt-3">{error}</div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setCurrentStep(Math.max(1, currentStep - 1))} disabled={currentStep === 1 || isCreating}>
                            Anterior
                        </button>
                        {currentStep < 6 ? (
                            <button type="button" className="btn btn-primary" onClick={() => setCurrentStep(Math.min(6, currentStep + 1))}>
                                Próximo
                            </button>
                        ) : (
                            <button ref={btnSave} type="button" className="btn btn-success" onClick={onCreateCompleteStrategy} disabled={isCreating}>
                                {isCreating ? 'Criando...' : 'Criar Estratégia Completa'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StrategyWizard;

